import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, TouchableOpacity, View, Pressable } from 'react-native';
import { FontFamily } from '../../../constants/fonts';
import { RcaMatrixRowData, RcaSubCategoryMap } from './types';
import SkeletonBlock from './SkeletonBlock';

interface RcaByCategoryHeatmapSectionProps {
  title: string;
  description: string;
  categories: string[];
  rcaCategories: string[];
  matrixRows: RcaMatrixRowData[];
  subCategoriesByCategory: RcaSubCategoryMap;
  subCategoryRcaData?: Record<string, Record<string, Record<string, number>>>;
  apiSubCategoriesByCategory?: Record<string, string[]>;

  /**
   * When true the heatmap grid is replaced by a shimmering placeholder grid of
   * the same dimensions, so the card keeps its size while data loads.
   */
  loading?: boolean;

  /** Number of placeholder RCA rows rendered while loading. */
  skeletonRowCount?: number;

  /** Number of placeholder category columns rendered while loading. */
  skeletonColumnCount?: number;
  /** Optional error message to display inside the card when the request failed. */
  error?: string | null;
  /** Called when the user taps the Retry button shown in the error state. */
  onRetry?: () => void;
  /** When true, shows the filter-hint empty message instead of the generic one. */
  hasActiveFilters?: boolean;
}

const HEAT_COLORS = ['#EAF1FB', '#D9E8FB', '#BCD7F5', '#7FB1E8', '#005CBC'];
const ROW_HEADER_WIDTH = 172;
const COLUMN_MIN_WIDTH = 108;
const GRID_GAP = 4;

const distributeByWeights = (total: number, weights: number[]): number[] => {
  const safeWeights = weights.map((item) => (item > 0 ? item : 0));
  const sum = safeWeights.reduce((acc, item) => acc + item, 0);
  if (total <= 0 || sum <= 0) {
    return safeWeights.map(() => 0);
  }

  const base = safeWeights.map((weight) => Math.floor((total * weight) / sum));
  let remainder = total - base.reduce((acc, item) => acc + item, 0);

  const order = safeWeights
    .map((weight, index) => ({ weight, index }))
    .sort((a, b) => b.weight - a.weight)
    .map((item) => item.index);

  let cursor = 0;
  while (remainder > 0 && order.length > 0) {
    const target = order[cursor % order.length];
    base[target] += 1;
    remainder -= 1;
    cursor += 1;
  }

  return base;
};

/**
 * Placeholder grid shown in place of the heatmap while data loads.
 * It mirrors the real grid structure (header row + data rows, same row-header
 * width and cell height) so the card keeps its exact footprint.
 */
const HeatmapSkeleton: React.FC<{
  rowCount: number;
  columnCount: number;
  columnWidth: number;
}> = ({ rowCount, columnCount, columnWidth }) => (
  <>
    <View style={styles.headerRow}>
      <View style={styles.rowHeaderCell} />
      {Array.from({ length: columnCount }).map((_, colIndex) => (
        <View
          key={`skeleton-column-${colIndex}`}
          style={[styles.columnHeaderCell, { width: columnWidth }]}
        >
          <SkeletonBlock style={styles.skeletonColumnHeader} />
        </View>
      ))}
    </View>

    {Array.from({ length: rowCount }).map((_, rowIndex) => (
      <View style={styles.dataRow} key={`skeleton-row-${rowIndex}`}>
        <View style={styles.rowHeaderCell}>
          <SkeletonBlock style={styles.skeletonRowHeader} />
        </View>

        {Array.from({ length: columnCount }).map((_, colIndex) => (
          <SkeletonBlock
            key={`skeleton-cell-${rowIndex}-${colIndex}`}
            style={[styles.skeletonHeatCell, { width: columnWidth }]}
          />
        ))}
      </View>
    ))}
  </>
);

const RcaByCategoryHeatmapSection: React.FC<RcaByCategoryHeatmapSectionProps> = ({
  title,
  description,
  categories,
  rcaCategories,
  matrixRows,
  subCategoriesByCategory,
  subCategoryRcaData,
  apiSubCategoriesByCategory,
  loading = false,
  skeletonRowCount = 6,
  skeletonColumnCount = 6,
  error = null,
  onRetry,
  hasActiveFilters = false,
}) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  const categoryValueByRca = useMemo(() => {
    const byRca = new Map<string, Record<string, number>>();

    matrixRows.forEach((row) => {
      const normalized: Record<string, number> = {};
      Object.keys(row.values).forEach((categoryKey) => {
        normalized[categoryKey] = Number(row.values[categoryKey] ?? 0);
      });
      byRca.set(row.rcaCategory, normalized);
    });

    return byRca;
  }, [matrixRows]);

  const columns = useMemo(() => {
    if (!selectedCategory) {
      return categories.map((item) => ({ key: item, label: item }));
    }

    // Prefer API sub-categories if available, fall back to breakdown sub-categories
    const apiSubs = apiSubCategoriesByCategory?.[selectedCategory] ?? [];
    if (apiSubs.length > 0) {
      return apiSubs.map((name) => ({ key: name, label: name }));
    }

    const subs = subCategoriesByCategory[selectedCategory] ?? [];
    if (!subs.length) {
      return [];
    }

    return subs.map((item) => ({ key: item.name, label: item.name }));
  }, [categories, selectedCategory, subCategoriesByCategory, apiSubCategoriesByCategory]);

  const valuesGrid = useMemo(() => {
    const grid: number[][] = rcaCategories.map(() => columns.map(() => 0));

    if (!selectedCategory) {
      rcaCategories.forEach((rcaName, rowIndex) => {
        const rowValues = categoryValueByRca.get(rcaName) ?? {};
        columns.forEach((column, colIndex) => {
          grid[rowIndex][colIndex] = Number(rowValues[column.key] ?? 0);
        });
      });

      return grid;
    }

    // Use actual sub-category RCA data if available
    if (subCategoryRcaData) {
      rcaCategories.forEach((rcaName, rowIndex) => {
        const categorySubValues = subCategoryRcaData[rcaName] ?? {};
        const categoryValues = categorySubValues[selectedCategory] ?? {};
        columns.forEach((column, colIndex) => {
          grid[rowIndex][colIndex] = Number(categoryValues[column.key] ?? 0);
        });
      });

      return grid;
    }

    // Fallback: distribute values if sub-category RCA data not available
    const subs = subCategoriesByCategory[selectedCategory] ?? [];
    const subWeights = subs.map((item) => item.count);

    rcaCategories.forEach((rcaName, rowIndex) => {
      const rowValues = categoryValueByRca.get(rcaName) ?? {};
      const totalForCategory = Number(rowValues[selectedCategory] ?? 0);
      const distributed = distributeByWeights(totalForCategory, subWeights);
      distributed.forEach((value, colIndex) => {
        grid[rowIndex][colIndex] = value;
      });
    });

    return grid;
  }, [categoryValueByRca, columns, rcaCategories, selectedCategory, subCategoriesByCategory]);

  const maxCellValue = useMemo(() => {
    const allValues = valuesGrid.flat();
    return Math.max(1, ...allValues);
  }, [valuesGrid]);

  const isEmpty =
    rcaCategories.length === 0 ||
    columns.length === 0 ||
    valuesGrid.every((row) => row.every((value) => value === 0));

  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [tooltipText, setTooltipText] = useState<string | null>(null);
  const [tooltipPagePos, setTooltipPagePos] = useState<{ x: number; y: number } | null>(null);

  const positionFromEvent = (e: any) => {
    try {
      const ne = e?.nativeEvent as any;
      if (ne && typeof ne.clientX === 'number' && typeof ne.clientY === 'number') {
        setTooltipPagePos({ x: ne.clientX, y: ne.clientY });
      }
    } catch (err) {
      // ignore
    }
  };

  const layout = useMemo(() => {
    // While loading there are no real columns yet, so size the grid from the
    // placeholder column count to keep the skeleton full-width and realistic.
    const columnCount = loading ? skeletonColumnCount : columns.length;
    if (columnCount <= 0) {
      return {
        gridWidth: Math.max(containerWidth, ROW_HEADER_WIDTH),
        columnWidth: COLUMN_MIN_WIDTH,
      };
    }

    const totalGapWidth = GRID_GAP * columnCount;
    const minGridWidth = ROW_HEADER_WIDTH + totalGapWidth + columnCount * COLUMN_MIN_WIDTH;
    const gridWidth = Math.max(containerWidth, minGridWidth);
    const availableColumnsWidth = Math.max(0, gridWidth - ROW_HEADER_WIDTH - totalGapWidth);
    const columnWidth = Math.max(COLUMN_MIN_WIDTH, availableColumnsWidth / columnCount);

    return {
      gridWidth,
      columnWidth,
    };
  }, [columns.length, containerWidth, loading, skeletonColumnCount]);

  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <View style={styles.titleWrap}>
          {selectedCategory ? (
            <TouchableOpacity style={styles.backArrow} onPress={() => setSelectedCategory(null)}>
              <Text style={styles.backArrowText}>{'<'}</Text>
            </TouchableOpacity>
          ) : null}
          <Text style={styles.cardTitle}>
            {selectedCategory ? `RCA by ${selectedCategory} Sub-Category` : title}
          </Text>
        </View>
        {loading && !description ? (
          <SkeletonBlock style={styles.skeletonNote} />
        ) : null}
        {!loading && description && <Text style={styles.cardNote}>{description}</Text>}
      </View>

      <View style={styles.heatWrap} onLayout={(event) => setContainerWidth(event.nativeEvent.layout.width)}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.heatScrollContent}>
          <View style={[styles.gridWrap, { width: layout.gridWidth }]}>
            {/*
              While loading, swap only the grid body for a placeholder of the
              same shape — the card shell, title and footer note stay mounted so
              nothing shifts once the real matrix arrives.
            */}
            {loading ? (
              <HeatmapSkeleton
                rowCount={skeletonRowCount}
                columnCount={skeletonColumnCount}
                columnWidth={layout.columnWidth}
              />
            ) : error ? (
              <View style={styles.errorWrap}>
                <Text style={styles.errorText}>{error}</Text>
                {!!onRetry && (
                  <TouchableOpacity onPress={onRetry} style={styles.retryButton} accessibilityRole="button" accessibilityLabel="Retry">
                    <Text style={styles.retryText}>↻  Retry</Text>
                  </TouchableOpacity>
                )}
              </View>
            ) : isEmpty ? (
              <View style={styles.noDataWrap}>
                <Text style={styles.noDataText}>
                  {hasActiveFilters
                    ? 'Try expanding your date range or removing some filters.'
                    : 'Try expanding your date range or removing some filters.'}
                </Text>
              </View>
            ) : (
              <>
            <View style={styles.headerRow}>
              <View style={styles.rowHeaderCell} />
              {columns.map((column) => {
                const clickable = !selectedCategory;
                return (
                  <TouchableOpacity
                    key={column.key}
                    disabled={!clickable}
                    onPress={() => setSelectedCategory(column.key)}
                    style={[styles.columnHeaderCell, { width: layout.columnWidth }]}
                  >
                    <Text style={[styles.columnHeaderText, clickable ? styles.columnHeaderLink : undefined]}>
                      {column.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            {rcaCategories.map((rcaName, rowIndex) => (
              <View style={styles.dataRow} key={rcaName}>
                <View style={styles.rowHeaderCell}>
                  <Text style={styles.rowHeaderText}>{rcaName}</Text>
                </View>

                {columns.map((column, colIndex) => {
                  const value = valuesGrid[rowIndex]?.[colIndex] ?? 0;
                  const ratio = value / maxCellValue;
                  const colorIndex =
                    value === 0 ? 0 : ratio < 0.25 ? 1 : ratio < 0.5 ? 2 : ratio < 0.75 ? 3 : 4;
                  const bgColor = HEAT_COLORS[colorIndex];
                  const textColor = colorIndex >= 3 ? '#FFFFFF' : '#2D2F33';

                  return (
                    <Pressable
                      key={`${rcaName}-${column.key}`}
                      style={[styles.heatCell, { width: layout.columnWidth, backgroundColor: bgColor }]}
                      onHoverIn={(e:any) => {
                          positionFromEvent(e);
                          setTooltipText(`${rcaName} / ${column.label}: ${value}`);
                          setTooltipVisible(true);
                        }}
                        onHoverOut={() => { setTooltipVisible(false); setTooltipPagePos(null); }}
                        onPressIn={(e:any) => {
                          positionFromEvent(e);
                          setTooltipText(`${rcaName} / ${column.label}: ${value}`);
                          setTooltipVisible(true);
                        }}
                        onPressOut={() => { setTooltipVisible(false); setTooltipPagePos(null); }}
                    >
                      <Text style={[styles.heatCellText, { color: textColor }]}>{value > 0 ? value : ''}</Text>
                    </Pressable>
                  );
                })}
              </View>
            ))}
              </>
            )}
          </View>
        </ScrollView>
      </View>

      {tooltipVisible && tooltipText ? (
        (() => {
          const page = tooltipPagePos;
          if (page && typeof page.x === 'number' && typeof page.y === 'number') {
            const padding = 8;
            const offsetX = 12;
            const offsetY = 36;
            const winW = typeof window !== 'undefined' ? window.innerWidth : 0;
            const winH = typeof window !== 'undefined' ? window.innerHeight : 0;
            const tooltipWidthEstimate = 220;
            let left = page.x + offsetX;
            if (winW) left = Math.min(left, Math.max(padding, winW - tooltipWidthEstimate - padding));
            let top = page.y - offsetY;
            if (winH) top = Math.min(Math.max(padding, top), winH - padding);

            return (
              <View pointerEvents="none" style={[styles.tooltipOverlay, { position: 'fixed', left, top, zIndex: 1000 }]}> 
                <View style={styles.tooltipBubble}>
                  <Text style={styles.tooltipText}>{tooltipText}</Text>
                </View>
              </View>
            );
          }

          return (
            <View style={styles.tooltipOverlay} pointerEvents="none">
              <View style={styles.tooltipBubble}>
                <Text style={styles.tooltipText}>{tooltipText}</Text>
              </View>
            </View>
          );
        })()
      ) : null}

      <Text style={styles.footerNote}>
        Darker cells carry a higher concentration of tickets. Select a category column to drill into its sub-categories.
      </Text>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#E8EDF3',
    padding: 20,
    marginBottom: 16,
  },
  cardHead: {
    marginBottom: 12,
    gap: 6,
  },
  titleWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  backArrow: {
    width: 26,
    height: 26,
    borderWidth: 1,
    borderColor: '#D0D5DD',
    borderRadius: 13,
    alignItems: 'center',
    justifyContent: 'center',
  },
  backArrowText: {
    fontSize: 14,
    color: '#5D636B',
    fontFamily: FontFamily.bold,
    marginTop: -1,
  },
  cardTitle: {
    fontSize: 16,
    fontFamily: FontFamily.bold,
    color: '#2D2F33',
  },
  cardNote: {
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#7A8088',
  },
  gridWrap: {
    gap: 4,
  },
  heatWrap: {
    overflow: 'hidden',
  },
  heatScrollContent: {
    minWidth: '100%',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  dataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  rowHeaderCell: {
    width: 172,
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'flex-end',
    paddingRight: 8,
  },
  rowHeaderText: {
    fontSize: 12,
    lineHeight: 16,
    color: '#2D2F33',
    fontFamily: FontFamily.medium,
    textAlign: 'right',
  },
  columnHeaderCell: {
    minHeight: 36,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  columnHeaderText: {
    fontSize: 11,
    lineHeight: 14,
    textAlign: 'center',
    color: '#6A6D71',
    fontFamily: FontFamily.bold,
  },
  columnHeaderLink: {
    color: '#005CBC',
    textDecorationLine: 'underline',
  },
  heatCell: {
    height: 36,
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  heatCellText: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
  },
  footerNote: {
    marginTop: 10,
    fontSize: 12,
    color: '#7A8088',
    fontFamily: FontFamily.regular,
  },

  // ─── Loading skeleton ────────────────────────────────────────────────────
  skeletonNote: {
    width: 240,
    height: 11,
    borderRadius: 4,
  },
  skeletonColumnHeader: {
    width: '80%',
    height: 11,
    borderRadius: 4,
  },
  skeletonRowHeader: {
    width: '85%',
    height: 12,
    borderRadius: 4,
  },
  skeletonHeatCell: {
    height: 36,
    borderRadius: 4,
  },
  tooltipOverlay: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -50 }, { translateY: -50 }],
    zIndex: 1000,
  },
  tooltipBubble: {
    backgroundColor: 'rgba(0,0,0,0.7)',
    borderRadius: 4,
    padding: 8,
  },
  tooltipText: {
    color: '#FFF',
    fontSize: 12,
    fontFamily: FontFamily.medium,
  },
  errorWrap: {
    minHeight: 160,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    color: '#B42318',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: 12,
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#1D4ED8',
    alignSelf: 'center',
  },
  retryText: {
    color: '#1D4ED8',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
  noDataWrap: {
    minHeight: 160,
    justifyContent: 'center',
    alignItems: 'center',
  },
  noDataText: {
    color: '#7A8088',
    fontSize: 14,
    fontFamily: FontFamily.medium,
    textAlign: 'center',
  },
});

export default RcaByCategoryHeatmapSection;






